import { playerPosition } from "./squadPlayers";
import { mulberry32 } from "./directSimulationCore";
export const SCORER_RULES = Object.freeze({
  version: 1,
  baseExposure: 0.1,
  exposure: 0.9,
  minutes: 90,
  smoothingMinutes: 450,
  goalsMultiplier: 8,
  unknownPosition: 0.85,
  positions: Object.freeze({
    TW: 0.01,
    DEF: 0.35,
    RV: 0.35,
    LV: 0.35,
    IV: 0.35,
    DM: 0.65,
    ZM: 0.85,
    MID: 0.85,
    ZOM: 1.2,
    RM: 1.2,
    LM: 1.2,
    RF: 1.2,
    LF: 1.2,
    HS: 1.5,
    ST: 1.5,
    ATT: 1.5,
  }),
});
const nonnegative = (v) =>
  Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : 0;
export function scorerDistribution(players, games = 1) {
  const seen = new Set();
  const rows = players
    .filter(
      (p) =>
        Number.isSafeInteger(p.id) &&
        p.id > 0 &&
        p.name &&
        p.name !== "–" &&
        !seen.has(p.id) &&
        seen.add(p.id)
    )
    .map((p) => {
      const s = p.statistics || {},
        minutes = nonnegative(s.appearances) * nonnegative(s.averageMinutes),
        goals = nonnegative(s.goals);
      const positionFactor =
        SCORER_RULES.positions[playerPosition(p).label] ??
        SCORER_RULES.unknownPosition;
      const exposure =
        SCORER_RULES.baseExposure +
        SCORER_RULES.exposure *
          Math.min(
            1,
            minutes / (SCORER_RULES.minutes * Math.max(1, nonnegative(games)))
          );
      const scoring =
        1 +
        ((SCORER_RULES.minutes * goals) /
          (minutes + SCORER_RULES.smoothingMinutes)) *
          SCORER_RULES.goalsMultiplier;
      return {
        playerId: p.id,
        name: p.name,
        teamId: p.teamId,
        actualGoals: s.goals ?? null,
        minutes,
        positionFactor,
        exposure,
        scoring,
        weight: positionFactor * exposure * scoring,
      };
    });
  const sum = rows.reduce((n, p) => n + p.weight, 0);
  let cumulative = 0;
  return rows.map((p, i) => ({
    ...p,
    probability: p.weight / sum,
    cumulative: i === rows.length - 1 ? 1 : (cumulative += p.weight / sum),
  }));
}
export function pickScorer(rows, rng) {
  if (!rows?.length) return null;
  const r = rng();
  return rows.find((p) => r < p.cumulative) || rows[rows.length - 1];
}
export function withScorers(result, distributions, seed) {
  const rng = mulberry32((seed ^ 0x53434f52) >>> 0);
  return {
    ...result,
    scorerVersion: SCORER_RULES.version,
    goalEvents: result.goalEvents.map((e) => {
      const p = pickScorer(distributions[e.side], rng);
      return {
        ...e,
        scorer: p
          ? { playerId: p.playerId, name: p.name, teamId: p.teamId }
          : null,
        scorerSimulated: true,
      };
    }),
  };
}
// Count each run independently; transfers within the season still describe one player.
export function seasonScorerCollector(teams, seed = 1) {
  const totals = new Map(),
    titles = new Map();
  let runs = 0,
    unknown = 0;
  const missingActual = Object.values(teams).some(
    (t) =>
      t.actualComplete === false ||
      !t.players.length ||
      t.players.some((p) => p.actualGoals == null)
  );
  for (const t of Object.values(teams))
    for (const p of [...t.players, ...(t.baseline || [])]) {
      const key = String(p.playerId),
        previous = totals.get(key);
      totals.set(
        key,
        previous
          ? {
              ...previous,
              teamName: [...new Set([previous.teamName, t.name])].join(" / "),
              actualGoals:
                previous.actualGoals == null || p.actualGoals == null
                  ? null
                  : previous.actualGoals + p.actualGoals,
            }
          : { ...p, teamName: t.name, simulated: 0 }
      );
    }
  return {
    startRun(index) {
      const counts = new Map(),
        rng = mulberry32((seed ^ index ^ 0x53434f52) >>> 0);
      return {
        fixture(f, r) {
          for (const [side, n] of [
            ["home", r.homeGoals],
            ["away", r.awayGoals],
          ])
            for (let i = 0; i < n; i++) {
              const p = pickScorer(teams[f[side + "_team_id"]]?.players, rng);
              if (p) {
                const key = String(p.playerId);
                counts.set(key, (counts.get(key) || 0) + 1);
              } else unknown++;
            }
        },
        finish() {
          runs++;
          let max = -1,
            winners = [];
          for (const [key, p] of totals) {
            const count = counts.get(key) || 0;
            p.simulated += count;
            const total = (p.actualGoals ?? 0) + count;
            if (total > max) {
              max = total;
              winners = [key];
            } else if (total === max) winners.push(key);
          }
          if (!missingActual)
            for (const key of winners)
              titles.set(key, (titles.get(key) || 0) + 1);
        },
      };
    },
    summary() {
      return {
        runs,
        unassigned: unknown / Math.max(1, runs),
        partial: missingActual || unknown > 0,
        rows: [...totals.entries()]
          .map(([key, p]) => ({
            ...p,
            simulated: p.simulated / Math.max(1, runs),
            total:
              p.actualGoals == null
                ? null
                : p.actualGoals + p.simulated / Math.max(1, runs),
            titleFrequency:
              missingActual || unknown > 0
                ? null
                : (titles.get(key) || 0) / Math.max(1, runs),
          }))
          .sort((a, b) => (b.total ?? b.simulated) - (a.total ?? a.simulated)),
      };
    },
  };
}
